import { Box, Paper, Stack, Typography } from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { WorkflowDetail } from "../../api/workflows";
import { MeResponse } from "../../api/me";
import {
  formatBytes,
  formatCount,
  formatDuration,
  summarizeExport,
} from "../../api/workflowSummary";

// Two-row results panel:
//
//   ┌── Funnel ─────────────────────────────────────────────┐
//   │  [matched]  →(dropped X)→  [kept]                     │
//   │             dropped breakdown                          │
//   └────────────────────────────────────────────────────────┘
//   ┌── Metrics ────────────────────────────────────────────┐
//   │  Duration  ·  Output  ·  Cost Estimate  ·  S3 GETs    │
//   └────────────────────────────────────────────────────────┘
//
// Reads from the workflow's aggregated counters via summarizeExport.
// Self-gates on non-export kinds and on workflows that haven't
// accumulated any counters yet.

interface Props {
  wf: WorkflowDetail;
  me: MeResponse | null;
}

const EXPORT_KINDS = new Set(["batch_export", "month_export"]);

export function ResultsPanel({ wf, me: _me }: Props) {
  if (!EXPORT_KINDS.has(wf.kind)) return null;
  const s = summarizeExport(wf);
  if (!s.hasData) return null;

  const succeeded = wf.status === "succeeded";
  const dropped =
    s.pagesFilteredBySearch + s.pagesSkippedNoTranscript + s.pagesFetchFailures;
  const inputPps =
    s.wallMs && s.wallMs > 0
      ? Math.round(
          (s.pagesDecompressed || s.pagesExported) / (s.wallMs / 1000),
        )
      : null;
  const outputPps =
    s.wallMs && s.wallMs > 0
      ? Math.round(s.pagesExported / (s.wallMs / 1000))
      : null;
  const keptPct =
    s.pagesMatched > 0
      ? Math.round((s.pagesExported / s.pagesMatched) * 1000) / 10
      : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        mb: 2,
        borderColor: succeeded ? "success.main" : "divider",
        borderWidth: succeeded ? 2 : 1,
      }}
    >
      <Typography variant="overline" color="text.secondary">
        Results
      </Typography>

      {/* Funnel — the dominant visual. Two big stages connected by
          an arrow that carries the drop count, with the per-reason
          breakdown underneath when something was dropped. */}
      <Stack
        direction="row"
        alignItems="stretch"
        spacing={2}
        sx={{ mt: 1.5, mb: 2, flexWrap: "wrap", rowGap: 2 }}
      >
        <FunnelStage
          label="Matched"
          value={formatCount(s.pagesMatched)}
          detail="metadata filter passed"
        />
        <FunnelArrow
          droppedValue={dropped > 0 ? formatCount(dropped) : null}
          droppedReasons={
            dropped > 0
              ? [
                  s.pagesFilteredBySearch > 0
                    ? `${formatCount(s.pagesFilteredBySearch)} search-filtered`
                    : null,
                  s.pagesSkippedNoTranscript > 0
                    ? `${formatCount(s.pagesSkippedNoTranscript)} no transcript`
                    : null,
                  s.pagesFetchFailures > 0
                    ? `${formatCount(s.pagesFetchFailures)} fetch failed`
                    : null,
                ]
                  .filter((x): x is string => x !== null)
                  .join(" · ")
              : null
          }
        />
        <FunnelStage
          label="Kept"
          value={formatCount(s.pagesExported)}
          detail={
            keptPct !== null ? `${keptPct.toFixed(1)}% of matched` : "exported"
          }
          emphasize
        />
      </Stack>

      {/* Supporting metrics row — duration / output / cost / s3. The
          numbers people glance at AFTER they've grasped the funnel. */}
      <Stack
        direction="row"
        spacing={4}
        sx={{
          flexWrap: "wrap",
          rowGap: 1.5,
          pt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <MetricTile
          label="Duration"
          value={formatDuration(s.wallMs)}
          detail={
            outputPps !== null
              ? inputPps !== null && inputPps !== outputPps
                ? `${inputPps.toLocaleString()} in/s · ${outputPps.toLocaleString()} out/s`
                : `${outputPps.toLocaleString()} pages/s`
              : null
          }
        />
        <MetricTile
          label="Output"
          value={formatBytes(s.bytesWritten)}
          detail={`${s.zipsWritten} zip${s.zipsWritten === 1 ? "" : "s"} · ${formatCount(s.pagesExported)} pages`}
        />
        <MetricTile
          label="Compute"
          value={`${Math.round(s.cpuSeconds).toLocaleString()}s CPU`}
          detail={`${Math.round(s.memoryGibSeconds).toLocaleString()} GiB·s memory`}
        />
        {s.s3GetRequests > 0 && (
          <MetricTile
            label="S3 GETs"
            value={formatCount(s.s3GetRequests)}
            detail={
              <>
                {Math.round(s.pagesMatched / s.s3GetRequests).toLocaleString()}{" "}
                pages/GET · {formatBytes(s.s3BytesFetched)} fetched
                {(s.s3BytesSkipped > 0 || s.s3BytesAvoided > 0) && (
                  <>
                    <br />
                    {formatBytes(s.s3BytesSkipped)} skipped (in-fetch) ·{" "}
                    {formatBytes(s.s3BytesAvoided)} avoided
                  </>
                )}
              </>
            }
          />
        )}
      </Stack>
    </Paper>
  );
}

function FunnelStage({
  label,
  value,
  detail,
  emphasize,
}: {
  label: string;
  value: string;
  detail: string;
  emphasize?: boolean;
}) {
  return (
    <Box
      sx={{
        flex: "1 1 200px",
        minWidth: 180,
        p: 2,
        borderRadius: 1.5,
        border: "2px solid",
        borderColor: emphasize ? "success.main" : "divider",
        backgroundColor: emphasize ? "success.50" : "background.default",
        textAlign: "center",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}
      >
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.5, lineHeight: 1.15, fontWeight: 600 }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 0.5 }}
      >
        {detail}
      </Typography>
    </Box>
  );
}

function FunnelArrow({
  droppedValue,
  droppedReasons,
}: {
  droppedValue: string | null;
  droppedReasons: string | null;
}) {
  return (
    <Box
      sx={{
        flex: "0 0 auto",
        minWidth: 140,
        maxWidth: 240,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        px: 1,
      }}
    >
      <ArrowRightAltIcon
        sx={{ fontSize: 48, color: "text.secondary", mb: -0.5 }}
      />
      {droppedValue && (
        <>
          <Typography
            variant="body2"
            color="error.main"
            sx={{ fontWeight: 600 }}
          >
            −{droppedValue} dropped
          </Typography>
          {droppedReasons && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 0.5, lineHeight: 1.3 }}
            >
              {droppedReasons}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

function MetricTile({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: React.ReactNode;
}) {
  return (
    <Box sx={{ minWidth: 140 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block" }}
      >
        {label}
      </Typography>
      <Typography variant="h6" sx={{ lineHeight: 1.3, fontWeight: 600 }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        component="div"
        sx={{ lineHeight: 1.4 }}
      >
        {detail}
      </Typography>
    </Box>
  );
}
